import React, { useEffect, useRef, useState } from 'react';
import * as faceapi from 'face-api.js';

const ProctoringSystem = ({ onViolation, onStatusChange, isActive }) => {
  const [isModelLoaded, setIsModelLoaded] = useState(false);
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const intervalRef = useRef(null);

  useEffect(() => {
    const loadModels = async () => {
      const MODEL_URL = process.env.PUBLIC_URL + '/models';
      try {
        console.log('Loading face-api models from:', MODEL_URL);
        await Promise.all([
          faceapi.nets.tinyFaceDetector.loadFromUri(MODEL_URL),
          faceapi.nets.faceLandmark68Net.loadFromUri(MODEL_URL),
        ]);
        setIsModelLoaded(true);
        if (onStatusChange) onStatusChange('Models loaded');
        console.log('Face-api models loaded successfully');
      } catch (error) {
        console.error('Error loading face-api models:', error);
        // Fallback: allow the interview to proceed even if face detection fails
        // but it will trigger violations if used. We should probably set isModelLoaded to false
        // and handle it gracefully in the interval.
      }
    };
    loadModels();
  }, []);

  useEffect(() => {
    if (isActive && isModelLoaded) {
      startCamera();
      startMonitoring();
    } else {
      stopMonitoring();
    }
    return () => stopMonitoring();
  }, [isActive, isModelLoaded]);

  const startCamera = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: true, audio: true });
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
      }
    } catch (err) {
      onViolation('camera_permission_denied', { error: err.message });
    }
  };

  const startMonitoring = () => {
    // 1. Tab Switching & Visibility
    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('blur', handleWindowBlur);
    
    // 2. Fullscreen enforcement
    document.addEventListener('fullscreenchange', handleFullscreenChange);

    // 3. Restricted Keys
    window.addEventListener('keydown', handleKeyDown);

    // 4. Copy/Paste Protection
    document.addEventListener('copy', preventDefault);
    document.addEventListener('paste', preventDefault);
    document.addEventListener('contextmenu', preventDefault);

    // 5. Face Detection Loop
    intervalRef.current = setInterval(async () => {
      if (videoRef.current && isModelLoaded) {
        try {
          // Use detectSingleFace with landmarks for better accuracy and "head turn" logic
          const detection = await faceapi.detectSingleFace(videoRef.current, new faceapi.TinyFaceDetectorOptions()).withFaceLandmarks();
          
          if (!detection) {
            onViolation('no_face_detected');
            return;
          }

          // If face is detected, notify success
          if (onStatusChange) onStatusChange('face_detected');

          // Check for multiple faces separately if needed, but detectSingleFace is more reliable for landmarks
          const allFaces = await faceapi.detectAllFaces(videoRef.current, new faceapi.TinyFaceDetectorOptions());
          if (allFaces.length > 1) {
            onViolation('multiple_faces_detected');
          }

          const landmarks = detection.landmarks;
          if (landmarks) {
            const noseTip = landmarks.getNose()[3]; // Tip of the nose
            const jawLeft = landmarks.getJawOutline()[0];
            const jawRight = landmarks.getJawOutline()[16];
            
            const noseX = noseTip.x;
            const leftX = jawLeft.x;
            const rightX = jawRight.x;

            const distToLeft = Math.abs(noseX - leftX);
            const distToRight = Math.abs(noseX - rightX);
            
            const leftRatio = distToLeft / (distToLeft + distToRight);
            
            // If nose is too close to either side of the jaw (e.g. < 12% or > 88%), it's a significant head turn
            if (leftRatio < 0.12 || leftRatio > 0.88) {
              onViolation('head_turn_complete', { direction: leftRatio < 0.12 ? 'left' : 'right' });
            }
          }
        } catch (err) {
          console.error("Proctoring detection error", err);
        }
      }
    }, 3000); // Increased frequency to 3 seconds for stricter monitoring
  };

  const stopMonitoring = () => {
    document.removeEventListener('visibilitychange', handleVisibilityChange);
    window.removeEventListener('blur', handleWindowBlur);
    document.removeEventListener('fullscreenchange', handleFullscreenChange);
    window.removeEventListener('keydown', handleKeyDown);
    document.removeEventListener('copy', preventDefault);
    document.removeEventListener('paste', preventDefault);
    document.removeEventListener('contextmenu', preventDefault);
    if (intervalRef.current) clearInterval(intervalRef.current);
    if (videoRef.current && videoRef.current.srcObject) {
      videoRef.current.srcObject.getTracks().forEach(track => track.stop());
    }
  };

  const handleVisibilityChange = () => {
    if (document.hidden) {
      onViolation('tab_switch');
      alert("Tab switching is not allowed. Your interview will be terminated.");
    }
  };

  const handleWindowBlur = () => {
    onViolation('window_switch');
    alert("Window switching is not allowed. Your interview will be terminated.");
  };

  const handleFullscreenChange = () => {
    if (!document.fullscreenElement) {
        onViolation('fullscreen_exit');
        if (window.confirm("Fullscreen exit is not allowed. Please stay in fullscreen mode. Click OK to return to fullscreen.")) {
            const element = document.documentElement;
            if (element.requestFullscreen) {
                element.requestFullscreen();
            } else if (element.mozRequestFullScreen) {
                element.mozRequestFullScreen();
            } else if (element.webkitRequestFullscreen) {
                element.webkitRequestFullscreen();
            } else if (element.msRequestFullscreen) {
                element.msRequestFullscreen();
            }
        }
    }
  };

  const preventDefault = (e) => {
    e.preventDefault();
    onViolation('restricted_action', { type: e.type });
  };

  const handleKeyDown = (e) => {
    const restrictedKeys = ['F12', 'PrintScreen'];
    const restrictedCombos = (e.ctrlKey || e.metaKey) && ['c', 'v', 'x', 'u', 's', 'p', 'a', 'shift'].includes(e.key.toLowerCase());
    
    if (restrictedKeys.includes(e.key) || restrictedCombos) {
      e.preventDefault();
      onViolation('restricted_key', { key: e.key });
    }
  };

  return (
    <div className="proctoring-container" style={{ position: 'fixed', bottom: 20, right: 20, width: 200, zIndex: 1000 }}>
      <video ref={videoRef} autoPlay muted width="200" height="150" style={{ borderRadius: 8, border: '2px solid #ccc' }} />
      <canvas ref={canvasRef} style={{ display: 'none' }} />
    </div>
  );
};

export default ProctoringSystem;
